import produce from 'immer';
import { getFlagOverrideValue } from 'src/utils/featureFlags';
import { create } from 'zustand';

// Base types for feature flags
export interface BaseFeatureFlag {
	/** @private Internal flag state - use flagEnabled() instead */
	readonly _enabled: boolean;
	rawPayload?: unknown;
	hasBeenSynced: boolean;
}

export interface BaseFeatureFlagWithPayload<T> extends BaseFeatureFlag {
	readonly payload: T;
}

// Special feature flags that need custom handling
export interface HighlightedAssetsFlag extends BaseFeatureFlag {
	assets: string[];
}
export interface SpotMarketsFlag extends BaseFeatureFlag {
	markets: string[];
}
export interface Maker20kRewardMarketIndexesFlag extends BaseFeatureFlag {
	marketIndexes: number[];
}
export interface IsolatedMarginEnabledMarketsFlag extends BaseFeatureFlag {
	marketSymbols: string[];
}
export interface AuctionParamsVersionFlag extends BaseFeatureFlag {
	version: number;
}

// Define the feature flag configurations
export interface FeatureFlagConfigs {
	HIGHLIGHTED_ASSETS: HighlightedAssetsFlag;
	ENABLE_UI_INVARIANT_CHECKS: BaseFeatureFlag;
	// Add new feature flags here by extending this interface
	// NEW_FEATURE_FLAG: BaseFeatureFlag;
	DISABLE_BLOCKCHAIN_EVENTS_FOR_MARKET_ORDER_TOASTS: BaseFeatureFlag;
	DLOG_CONSOLE_ENABLED: BaseFeatureFlag;
	SHOWN_SPOT_MARKETS: SpotMarketsFlag;
	FORCE_ORDER_PARAMS_FALLBACK: BaseFeatureFlag;
	MAKER_20K_REWARD_MARKET_INDEXES: Maker20kRewardMarketIndexesFlag;
	AUCTION_PARAMS_VERSION: AuctionParamsVersionFlag;
	DISABLE_WEBSOCKET_DLOB_SOURCE: BaseFeatureFlag;
	ENABLE_EMBEDDED_WALLET: BaseFeatureFlag;
	ENABLE_PRIVY_NATIVE_ACCOUNTS: BaseFeatureFlag;
	DISABLE_TRANSACTION_AUTO_SIGNING: BaseFeatureFlag;
	ENABLE_SEND_TRANSACTION_PROXY: BaseFeatureFlag;
	ENABLE_ISOLATED_POSITIONS: BaseFeatureFlag;
	ISOLATED_MARGIN_ENABLED_MARKETS: IsolatedMarginEnabledMarketsFlag;
	/** Deposit modal "Deposit from other sources" (Fun.xyz). Use PostHog Release conditions to enable for select users. */
	ENABLE_DEPOSIT_FROM_OTHER_SOURCES: BaseFeatureFlag;
	/** Elfa Apollo AI widget. Use PostHog Release conditions to enable progressively. */
	ENABLE_ELFA_WIDGET: BaseFeatureFlag;
	/** Gates UI access to wallets listed in the private_beta_wallet_whitelist database table. */
	ENABLE_PRIVATE_BETA_WALLET_WHITELIST: BaseFeatureFlag;
	/** Private beta mode: caps per-subaccount deposit value and requires a risk acknowledgment while Velocity is under audit. */
	ENABLE_PRIVATE_BETA: BaseFeatureFlag;
}

// Derive the feature flag key type from the configs
export type FeatureFlagKey = keyof FeatureFlagConfigs;

// Type helper to get the flag type for a specific key
export type FlagTypeForKey<K extends FeatureFlagKey> = FeatureFlagConfigs[K];

// The store's flag state type
type FlagState = {
	[K in FeatureFlagKey]: FlagTypeForKey<K>;
};

// Default values for all feature flags
export const FEATURE_FLAG_DEFAULTS: FlagState = {
	HIGHLIGHTED_ASSETS: {
		_enabled: false,
		assets: [],
		hasBeenSynced: false,
	},
	ENABLE_UI_INVARIANT_CHECKS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_BLOCKCHAIN_EVENTS_FOR_MARKET_ORDER_TOASTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DLOG_CONSOLE_ENABLED: {
		_enabled: false,
		hasBeenSynced: false,
	},
	SHOWN_SPOT_MARKETS: {
		_enabled: false,
		markets: ['SOL', 'DRIFT'],
		hasBeenSynced: false,
	},
	FORCE_ORDER_PARAMS_FALLBACK: {
		_enabled: false,
		hasBeenSynced: false,
	},
	MAKER_20K_REWARD_MARKET_INDEXES: {
		_enabled: false,
		marketIndexes: [13, 71, 23, 4, 75, 59, 9, 7, 30, 62], // Default to the same values as REWARD_MARKETS_20K
		hasBeenSynced: false,
	},
	AUCTION_PARAMS_VERSION: {
		_enabled: false,
		version: 1,
		hasBeenSynced: false,
	},
	DISABLE_WEBSOCKET_DLOB_SOURCE: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_EMBEDDED_WALLET: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_PRIVY_NATIVE_ACCOUNTS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	DISABLE_TRANSACTION_AUTO_SIGNING: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_SEND_TRANSACTION_PROXY: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_ISOLATED_POSITIONS: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ISOLATED_MARGIN_ENABLED_MARKETS: {
		_enabled: false,
		marketSymbols: [],
		hasBeenSynced: false,
	},
	ENABLE_DEPOSIT_FROM_OTHER_SOURCES: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_ELFA_WIDGET: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_PRIVATE_BETA_WALLET_WHITELIST: {
		_enabled: false,
		hasBeenSynced: false,
	},
	ENABLE_PRIVATE_BETA: {
		_enabled: false,
		hasBeenSynced: false,
	},
};

export interface FeatureFlagStore {
	set: (x: (s: FeatureFlagStore) => void) => void;
	get: () => FeatureFlagStore;
	flags: FlagState;
	customOverrides: Set<FeatureFlagKey>; // Custom overrides from API
	flagEnabled: (flagKey: FeatureFlagKey, skipOverride?: boolean) => boolean;
	getFlagPayload: <K extends FeatureFlagKey>(
		flagKey: K
	) => FlagTypeForKey<K>['rawPayload'];
}

const useFeatureFlagStore = create<FeatureFlagStore>((set, get) => ({
	set: (fn) => set(produce(fn)),
	get: () => get(),
	flags: FEATURE_FLAG_DEFAULTS,
	customOverrides: new Set<FeatureFlagKey>(),
	flagEnabled: (flagKey: FeatureFlagKey, ignoreOverride?: boolean) => {
		const baseValue = get().flags[flagKey]?._enabled ?? false;

		if (ignoreOverride) return baseValue;

		// Priority 1: localStorage override (dev override - highest priority)
		const flagOverride = getFlagOverrideValue(flagKey);
		if (flagOverride !== undefined) return flagOverride;

		// Priority 2: Custom API override
		const customOverrides = get().customOverrides;
		if (customOverrides.has(flagKey)) {
			// Custom override enables the flag regardless of base value
			return true;
		}

		// Priority 3: Base PostHog value (lowest priority)
		return baseValue;
	},
	getFlagPayload: <K extends FeatureFlagKey>(flagKey: K) =>
		get().flags[flagKey]?.rawPayload,
}));

export default useFeatureFlagStore;
